import { useMemo, useState } from "react"
import { Link, Navigate, useNavigate, useParams } from "@tanstack/react-router"
import { formatDistanceToNow } from "date-fns"
import { IconArrowRotateClockwiseOutline18 as Refresh } from "nucleo-ui-outline-18"

import type { ToolPolicies } from "@productlane/lib/src/browser"
import { humanizeToolName } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { AlertDialog, Button, Dot, Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { SettingsHeader } from "../../settings-header"
import { SettingItem } from "../../settings-switches/setting-item"
import {
	SETTINGS_CARD_CLASS,
	SETTINGS_INSET_CLASS,
	SettingsContainer,
} from "../../settings-switches/settings-container"
import { SettingsSection } from "../../settings-switches/settings-section"
import { McpToolAccessList } from "../sidebar-section-mcp-tools"
import { AccessControl, HTTP_ACCESS_OPTIONS } from "./access-control"
import { ConnectorForm } from "./connector-form"
import { ConnectorLogoTile } from "./connector-logo-tile"
import { useConnectorSummaries } from "./connector-status"

const RECENT_CALLS = 8

export function ConnectorPage() {
	const { connectorId } = useParams({ strict: false })
	const workspace = useZeroWorkspace()
	const zero = useZero()
	const navigate = useNavigate()
	const summaries = useConnectorSummaries()
	const [refreshToken, setRefreshToken] = useState(0)
	const [confirmDisconnect, setConfirmDisconnect] = useState(false)

	const sections = useMemo(
		() => workspace?.sidebarSections ?? [],
		[workspace?.sidebarSections],
	)
	const section = sections.find((entry) => entry.id === connectorId)

	const reusableHeaders = useMemo(
		() =>
			sections
				.filter((entry) => entry.id !== connectorId)
				.flatMap((entry) =>
					entry.sidebarSectionHeaders.map((header) => ({
						headerId: header.id,
						key: header.key,
						sectionTitle: entry.title,
					})),
				),
		[sections, connectorId],
	)

	if (!workspace) return null
	if (!section) return <Navigate to="/settings/custom-connectors" replace />

	const summary = summaries.get(section.id) ?? null
	const signedOut = summary?.health === "signed_out"
	const isMcp = section.type === "MCP"
	let host = section.url
	try {
		host = new URL(section.url).host
	} catch {
		// A malformed URL still shows as typed.
	}

	return (
		<>
			<MetaInfo title={section.title} />
			<SettingsHeader
				title={section.title}
				titlePrefix={
					<ConnectorLogoTile
						url={section.url}
						logoUrl={section.logoUrl ?? null}
						type={section.type}
					/>
				}
				subtitle={isMcp ? `MCP server at ${host}` : `Your API at ${host}`}
				action={
					<RoleCheck not={["VIEWER"]}>
						<Button
							variant="outline"
							onClick={() => setConfirmDisconnect(true)}
						>
							Disconnect
						</Button>
					</RoleCheck>
				}
			/>

			{signedOut ? (
				<SettingsContainer className="mb-8">
					<div className="flex items-start justify-between gap-4">
						<div className="min-w-0">
							<div className="text-sm font-medium">
								{section.title} rejected the saved API key
							</div>
							<div className="mt-0.5 text-xs tracking-tight text-secondary">
								Enter a new key below so the agent can use it again.
							</div>
							{summary?.lastError ? (
								<div className="mt-2 line-clamp-3 break-words text-xs text-tertiary">
									{summary.lastError}
								</div>
							) : null}
						</div>
						<Button
							size="xs"
							className="flex-none"
							onClick={() =>
								document
									.getElementById("connection")
									?.scrollIntoView({ behavior: "smooth" })
							}
						>
							Enter a new key
						</Button>
					</div>
				</SettingsContainer>
			) : null}

			<RoleCheck not={["VIEWER"]}>
				<SettingsSection
					title="Permissions"
					description={
						isMcp
							? "Choose who can use each action: the agent and your team, only your team, or no one."
							: "Choose whether the agent can use it when it replies to customers."
					}
					className="mb-4 mt-0"
					action={
						isMcp ? (
							<Button
								variant="ghost"
								size="xs"
								Icon={Refresh}
								onClick={() => setRefreshToken((token) => token + 1)}
							>
								Refresh actions
							</Button>
						) : null
					}
				/>
				{isMcp ? (
					<McpToolAccessList
						sectionId={section.id}
						connectorTitle={section.title}
						allowedMcpTools={section.allowedMcpTools ?? []}
						blockedInternalMcpTools={section.blockedInternalMcpTools ?? []}
						toolPolicies={(section.toolPolicies ?? null) as ToolPolicies | null}
						refreshToken={refreshToken}
					/>
				) : (
					<SettingsContainer>
						<SettingItem
							title="Who can use it"
							description="Drafts and the AI Assistant always read this endpoint."
							action={
								<AccessControl
									name={`http-access-${section.id}`}
									label={`Access for ${section.title}`}
									value={section.availableToAgent ? "agent" : "team"}
									options={HTTP_ACCESS_OPTIONS}
									onChange={(access) => {
										zero.mutate(
											mutators.sidebarSections.setAccess({
												id: section.id,
												availableToAgent: access === "agent",
											}),
										)
										analytics.track("sidebar-section-access-change")
									}}
								/>
							}
						/>
						<SidebarVisibilityItem sectionId={section.id} />
					</SettingsContainer>
				)}

				{isMcp ? (
					<>
						<SettingsSection
							title="Who the agent can look up"
							className="mb-4"
						/>
						<SettingsContainer>
							<SettingItem
								title="Answer visitors who haven't verified their email"
								description="Turn on only for public data like docs or a status page."
								action={
									<Switch
										name="reachableWithoutIdentity"
										checked={section.reachableWithoutIdentity}
										onCheckedChange={(reachable) => {
											zero.mutate(
												mutators.sidebarSections.setPublicKnowledge({
													id: section.id,
													reachableWithoutIdentity: reachable,
												}),
											)
											analytics.track("sidebar-section-public-knowledge-change")
										}}
									/>
								}
							/>
						</SettingsContainer>
					</>
				) : null}
			</RoleCheck>

			<RoleCheck not={["VIEWER"]}>
				<div id="connection" className="scroll-mt-8">
					<SettingsSection title="Connection" className="mb-4" />
					<ConnectorForm
						key={section.id}
						type={section.type}
						data={{
							id: section.id,
							title: section.title,
							url: section.url,
							description: section.description ?? null,
							type: section.type,
							headers: section.sidebarSectionHeaders,
							oauthTokenUrl: section.oauthTokenUrl ?? null,
							oauthClientId: section.oauthClientId ?? null,
							oauthScope: section.oauthScope ?? null,
							oauthAudience: section.oauthAudience ?? null,
						}}
						nextOrder={sections.length}
						reusableHeaders={reusableHeaders}
						onSaved={() => setRefreshToken((token) => token + 1)}
					/>
				</div>
			</RoleCheck>

			<RecentCalls sectionId={section.id} />

			<AlertDialog
				isOpen={confirmDisconnect}
				onOpenChange={setConfirmDisconnect}
				confirmText="Disconnect"
				title={`Disconnect ${section.title}?`}
				description="The agent and your team lose its actions. Past calls stay in the history."
				onCancel={() => setConfirmDisconnect(false)}
				onConfirm={() => {
					zero.mutate(mutators.sidebarSections.delete({ id: section.id }))
					analytics.track("sidebar-section-delete")
					void navigate({ to: "/settings/custom-connectors", replace: true })
				}}
			/>
		</>
	)
}

function RecentCalls({ sectionId }: { sectionId: string }) {
	const query = api.mcpActivity.workspaceActivity.useQuery({
		sectionId,
		days: 14,
		limit: RECENT_CALLS,
	})
	const rows = query.data?.rows ?? []

	return (
		<>
			<SettingsSection
				title="Recent calls"
				className="mb-4"
				action={
					<Link
						to="/settings/custom-connectors/history"
						className="text-xs text-secondary hover:text-primary"
					>
						View all
					</Link>
				}
			/>
			<div
				className={cn(
					SETTINGS_CARD_CLASS,
					SETTINGS_INSET_CLASS,
					"divide-y divide-gray-200 dark:divide-gray-700/80",
				)}
			>
				{query.isLoading ? (
					<div className="h-12 px-4 py-3" />
				) : rows.length === 0 ? (
					<div className="px-4 py-3 text-sm text-secondary">
						No calls in the last 14 days.
					</div>
				) : (
					rows.map((row) => {
						const ok = row.outcome === "ok"
						return (
							<div key={row.id} className="flex items-start gap-3 px-4 py-2.5">
								<Dot
									color={ok ? "green" : "red"}
									size="sm"
									className="mt-1.5 flex-none"
								/>
								<div className="min-w-0 flex-1">
									<div className="truncate text-sm">
										{humanizeToolName(row.toolName)}
									</div>
									{!ok && row.detail ? (
										<div className="mt-0.5 line-clamp-2 text-xs tracking-tight text-secondary">
											{row.detail}
										</div>
									) : null}
								</div>
								<span className="flex-none text-xs text-tertiary">
									{formatDistanceToNow(new Date(row.createdAt), {
										addSuffix: true,
									})}
								</span>
							</div>
						)
					})
				)}
			</div>
		</>
	)
}

// Written over tRPC: the column stays out of Zero, so adding it needed no
// publication change.
function SidebarVisibilityItem({ sectionId }: { sectionId: string }) {
	const utils = api.useUtils()
	const hidden = api.customData.hiddenSidebarSections.useQuery()
	const setShow = api.customData.setShowInSidebar.useMutation({
		onMutate: ({ showInSidebar }) => {
			const previous = utils.customData.hiddenSidebarSections.getData()
			utils.customData.hiddenSidebarSections.setData(undefined, (ids = []) =>
				showInSidebar
					? ids.filter((id) => id !== sectionId)
					: [...ids.filter((id) => id !== sectionId), sectionId],
			)
			return { previous }
		},
		onError: (_error, _input, context) => {
			utils.customData.hiddenSidebarSections.setData(
				undefined,
				context?.previous,
			)
			toast.error({ title: "The sidebar setting did not save" })
		},
		onSettled: () => void utils.customData.hiddenSidebarSections.invalidate(),
	})
	const showInSidebar = !(hidden.data ?? []).includes(sectionId)

	return (
		<SettingItem
			title="Show in thread sidebar"
			description="The AI reads it even when the card is hidden."
			action={
				<Switch
					name="showInSidebar"
					checked={showInSidebar}
					disabled={hidden.isLoading}
					onCheckedChange={(checked) => {
						setShow.mutate({ sectionId, showInSidebar: checked })
						analytics.track("sidebar-section-visibility-change")
					}}
				/>
			}
		/>
	)
}
